import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── Fund Overview Spectrum ──
function FundSpectrum() {
  const [active, setActive] = useState(null);
  const funds = [
    {
      icon: "💵", name: "Money Market", color: green, riskScore: 8,
      tagline: "Safety first. Capital preservation.",
      objective: "Safety of principal, liquidity, regular income.",
      holds: "T-Bills, Commercial Paper, Bankers' Acceptances (≤ 364 days)",
      risk: "Inflation risk — returns may trail the cost of living",
      special: "Unit price fixed at $10. NOT CDIC insured. Max weighted avg maturity: 90 days.",
      pills: ["Lowest Risk", "$10 Unit", "Not CDIC Insured", "90-Day Max WAM"],
    },
    {
      icon: "🏠", name: "Mortgage", color: blue2, riskScore: 28,
      tagline: "Monthly income from mortgage interest.",
      objective: "Current income, capital preservation, possible capital gains if rates fall.",
      holds: "Residential & commercial mortgages (mostly NHA-insured)",
      risk: "Interest rate risk — if rates rise, older lower-rate mortgages decline in value",
      special: "Inverse relationship with interest rates — acts like a bond fund. NHA insurance protects against default.",
      pills: ["Monthly Income", "NHA Insured", "Interest Rate Risk", "Inverse Rates"],
    },
    {
      icon: "📄", name: "Bond Funds", color: gold, riskScore: 50,
      tagline: "Bonds > 1 year. Duration is the key metric.",
      objective: "Regular income and capital gains when rates fall.",
      holds: "Government & corporate bonds (maturity > 1 year)",
      risk: "Interest rate risk — amplified by duration. The higher the duration, the more sensitive.",
      special: "Short-term bond fund (< 5 yr): low duration, low volatility. Global bond fund: adds currency risk.",
      pills: ["Duration Driven", "Interest Rate Risk", "Capital Gains Potential", "Short / Long / Global"],
    },
    {
      icon: "⚖️", name: "Preferred Dividend", color: purple, riskScore: 65,
      tagline: "Tax-efficient income via the Dividend Tax Credit.",
      objective: "High tax-efficient monthly income, capital preservation.",
      holds: "Preferred shares of Canadian corporations",
      risk: "Higher than bond funds — equity ranking in bankruptcy, plus interest rate sensitivity",
      special: "Income taxed as dividends (eligible for DTC), not interest. Best for non-registered accounts of high-income earners.",
      pills: ["Dividend Tax Credit", "Tax-Efficient", "Canadian Pref Shares", "Non-Reg Accounts"],
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      {/* Risk spectrum bar */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
          <span>← Lower Risk / Return</span><span>Higher Risk / Return →</span>
        </div>
        <div style={{ height: 10, borderRadius: 5, background: `linear-gradient(90deg, ${green}, ${blue2}, ${gold}, ${purple})`, marginBottom: 10, position: "relative" }}>
          {funds.map((f, i) => (
            <div key={i} style={{ position: "absolute", top: "50%", left: `${f.riskScore}%`, transform: "translate(-50%,-50%)", width: 16, height: 16, borderRadius: "50%", background: f.color, border: `2px solid ${navy}`, cursor: "pointer", boxShadow: active === i ? `0 0 10px ${f.color}` : "none", transition: "box-shadow 0.2s" }} onClick={() => setActive(active === i ? null : i)} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          {funds.map((f, i) => (
            <button key={i} onClick={() => setActive(active === i ? null : i)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "center", padding: "4px 6px" }}>
              <div style={{ fontSize: "1.1rem" }}>{f.icon}</div>
              <div style={{ fontSize: "0.65rem", color: active === i ? f.color : muted, fontWeight: active === i ? 700 : 400, transition: "color 0.2s" }}>{f.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {active !== null && (
        <div style={{ background: navy4, border: `1px solid ${funds[active].color}44`, borderRadius: 8, padding: 24, animation: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "1.6rem" }}>{funds[active].icon}</span>
            <div>
              <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white }}>{funds[active].name} Fund</div>
              <div style={{ fontSize: "0.78rem", color: funds[active].color, fontStyle: "italic" }}>{funds[active].tagline}</div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
            {[["Objective", funds[active].objective, green], ["Holds", funds[active].holds, blue2], ["Key Risk", funds[active].risk, red], ["Key Feature", funds[active].special, funds[active].color]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 90, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
                <div style={{ fontSize: "0.84rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
              </div>
            ))}
          </div>
          <div>{funds[active].pills.map((p, i) => <Pill key={i} text={p} color={funds[active].color} />)}</div>
        </div>
      )}
    </div>
  );
}

// ── Section 1: Money Market ──
function MoneyMarketDetail() {
  const [open, setOpen] = useState(null);
  const items = [
    { icon: "🏛️", name: "Treasury Bills (T-Bills)", color: green, risk: "Risk-Free", desc: "Issued by federal and provincial governments. The safest money market instrument. Sold at a discount, mature at par." },
    { icon: "📝", name: "Commercial Paper", color: gold, risk: "Low-Moderate", desc: "Unsecured short-term promissory notes issued by large corporations. Higher yield than T-bills due to slightly higher credit risk." },
    { icon: "🏦", name: "Bankers' Acceptances (BAs)", color: blue2, risk: "Low", desc: "Short-term corporate debt guaranteed by a chartered bank. Lower risk than commercial paper; common in trade finance." },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Unit Price", value: "$10.00", icon: "💲", color: gold, sub: "Fixed — rarely changes" },
          { label: "Max Maturity", value: "364 days", icon: "📅", color: blue2, sub: "Any single security" },
          { label: "Wtd Avg Maturity", value: "90 days", icon: "⏱️", color: green, sub: "Portfolio max (NI 81-102)" },
          { label: "CDIC Insured?", value: "NO", icon: "🚫", color: red, sub: "Mutual funds never are" },
        ].map((card, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 16px", textAlign: "center" }}>
            <div style={{ fontSize: "1.3rem", marginBottom: 6 }}>{card.icon}</div>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.2rem", fontWeight: 700, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: "0.72rem", color: muted, marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.88rem", color: muted, marginBottom: 10 }}>Portfolio Holdings (governed by NI 81-102)</div>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((item, i) => (
          <div key={i} onClick={() => setOpen(open === i ? null : i)}
            style={{ background: open === i ? navy4 : navy3, border: `1px solid ${open === i ? item.color + "44" : border}`, borderRadius: 8, padding: "14px 18px", cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: "1.2rem" }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{item.name}</div>
                  <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: item.color, background: `${item.color}18`, border: `1px solid ${item.color}33` }}>{item.risk}</span>
                </div>
              </div>
              <span style={{ color: open === i ? item.color : muted, fontSize: "1rem", transition: "transform 0.25s", display: "inline-block", transform: open === i ? "rotate(90deg)" : "none" }}>›</span>
            </div>
            {open === i && <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${border}` }}>{item.desc}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Yield Calculators ──
function MoneyMarketYieldCalc() {
  const [initialNAV, setInitialNAV] = useState(10000000);
  const [income, setIncome] = useState(1500);
  const [result, setResult] = useState(null);

  function calc() {
    const init = parseFloat(initialNAV) || 0;
    const inc = parseFloat(income) || 0;
    const endNAV = init + inc;
    const sevenDay = endNAV / init - 1;
    const currentYield = sevenDay * (365 / 7);
    const effectiveYield = Math.pow(1 + sevenDay, 365 / 7) - 1;
    setResult({ init, inc, endNAV, sevenDay, currentYield: (currentYield * 100).toFixed(4), effectiveYield: (effectiveYield * 100).toFixed(4), sevenDayPct: (sevenDay * 100).toFixed(6) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Money Market Yield Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "8px 14px", marginBottom: 20, lineHeight: 1.9 }}>
        7-Day Yield = (Ending NAV ÷ Initial NAV) − 1<br />
        Current Yield = 7-Day Yield × (365 ÷ 7) &nbsp;[no compounding]<br />
        Effective Yield = (1 + 7-Day Yield)^(365/7) − 1 &nbsp;[weekly compounding]
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[["Initial NAV ($)", initialNAV, setInitialNAV], ["7-Day Net Income ($)", income, setIncome]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate Yields →</button>

      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[
              { label: "7-Day Yield", value: `${result.sevenDayPct}%`, color: muted },
              { label: "Current Yield", value: `${result.currentYield}%`, color: blue2, sub: "No compounding" },
              { label: "Effective Yield", value: `${result.effectiveYield}%`, color: gold, sub: "Weekly compounding" },
            ].map((card, i) => (
              <div key={i} style={{ background: navy4, border: `1px solid ${card.color}33`, borderRadius: 8, padding: "14px 12px", textAlign: "center" }}>
                <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.color, marginBottom: 6 }}>{card.label}</div>
                <div style={{ fontFamily: "monospace", fontSize: "1.1rem", fontWeight: 700, color: white }}>{card.value}</div>
                {card.sub && <div style={{ fontSize: "0.68rem", color: muted, marginTop: 4 }}>{card.sub}</div>}
              </div>
            ))}
          </div>
          <div style={{ background: navy4, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, marginBottom: 10 }}>Step-by-Step Working</div>
            {[
              `Ending NAV: $${result.init.toLocaleString()} + $${result.inc.toLocaleString()} = $${result.endNAV.toLocaleString()}`,
              `7-Day Yield: $${result.endNAV.toLocaleString()} ÷ $${result.init.toLocaleString()} − 1 = ${result.sevenDayPct}%`,
              `Current Yield: ${result.sevenDayPct}% × (365÷7) = ${result.currentYield}% [simple annualization]`,
              `Effective Yield: (1 + ${result.sevenDayPct}%)^52.14 − 1 = ${result.effectiveYield}% [weekly compounding]`,
              `Effective Yield (${result.effectiveYield}%) > Current Yield (${result.currentYield}%) — always, because compounding adds extra return.`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: i === 4 ? gold : textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section 2: Mortgage Funds ──
function MortgageTypes() {
  const [active, setActive] = useState(0);
  const types = [
    {
      label: "🔓 Open Mortgage", color: blue2,
      rows: [
        ["Repayment", "Can be repaid at any time without penalty"],
        ["Interest Rate", "Lower — lender takes on prepayment risk"],
        ["Prepayment Risk", "Higher — borrower may refinance at lower rates; fund loses the higher-yield asset"],
        ["Best For", "Borrowers expecting to move or refinance soon"],
      ],
    },
    {
      label: "🔒 Closed Mortgage", color: gold,
      rows: [
        ["Repayment", "Cannot be repaid early without a penalty"],
        ["Interest Rate", "Higher — compensates for the lock-in period"],
        ["Prepayment Risk", "Lower — fund retains the mortgage for its full term"],
        ["Best For", "Borrowers seeking rate certainty and no movement plans"],
      ],
    },
    {
      label: "🏛️ NHA Insured", color: green,
      rows: [
        ["Insurance", "Guaranteed by the federal government under the National Housing Act"],
        ["Protection", "Against borrower default — lender is reimbursed if borrower stops paying"],
        ["Risk Reduction", "Most mortgage funds hold NHA-insured mortgages to minimize default risk"],
        ["Cost", "Borrower pays insurance premium; lender gets near-risk-free mortgage"],
      ],
    },
  ];
  const t = types[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {types.map((ty, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 18px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? ty.color + "44" : border}`, borderBottom: active === i ? `2px solid ${ty.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? ty.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>{ty.label}</button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${t.color}22`, borderRadius: "0 0 8px 8px", padding: 22 }}>
        <div style={{ display: "grid", gap: 8 }}>
          {t.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: t.color, width: 120, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MortgageRateViz() {
  const [rates, setRates] = useState("rising");
  const config = {
    rising: { label: "Rates Rising ↑", navDir: "↓ Falls", navColor: red, reason: "Existing mortgages in the fund were written at lower rates. Newer mortgages offer higher yields — making the old ones less valuable. Fund NAV drops.", icon: "📉" },
    falling: { label: "Rates Falling ↓", navDir: "↑ Rises", navColor: green, reason: "Existing mortgages in the fund carry above-market rates. They are now more valuable relative to new mortgages. Fund NAV rises — potential capital gain.", icon: "📈" },
  };
  const cfg = config[rates];
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 14 }}>🏠 Mortgage Fund NAV — Rate Sensitivity</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {Object.entries(config).map(([key, val]) => (
          <button key={key} onClick={() => setRates(key)} style={{ padding: "8px 18px", background: rates === key ? navy4 : navy3, border: `1px solid ${rates === key ? (key === "rising" ? red : green) + "55" : border}`, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: rates === key ? (key === "rising" ? red : green) : muted, transition: "all 0.2s" }}>{val.label}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ background: navy4, borderRadius: 8, padding: "18px 22px", textAlign: "center", minWidth: 120 }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Mortgage Rates</div>
          <div style={{ fontSize: "2rem" }}>{cfg.icon}</div>
          <div style={{ fontFamily: "monospace", fontSize: "0.9rem", color: rates === "rising" ? red : green, fontWeight: 700, marginTop: 4 }}>{cfg.label}</div>
        </div>
        <div style={{ fontSize: "1.5rem", color: muted }}>→</div>
        <div style={{ background: navy4, borderRadius: 8, padding: "18px 22px", textAlign: "center", minWidth: 120 }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Fund NAV</div>
          <div style={{ fontFamily: "monospace", fontSize: "1.5rem", fontWeight: 700, color: cfg.navColor }}>{cfg.navDir}</div>
        </div>
        <div style={{ flex: 1, background: `${cfg.navColor}0d`, border: `1px solid ${cfg.navColor}22`, borderRadius: 8, padding: "14px 18px", minWidth: 180 }}>
          <div style={{ fontSize: "0.82rem", color: textCol, lineHeight: 1.7 }}>{cfg.reason}</div>
        </div>
      </div>
    </div>
  );
}

// ── Section 3: Bond Funds / Duration ──
function DurationStrategy() {
  const [expectation, setExpectation] = useState("fall");
  const configs = {
    fall: {
      label: "Expect Rates to FALL", color: green, icon: "📉",
      strategy: "Buy long-term bonds → Increase Duration",
      why: "Long-duration bonds benefit most from falling rates — their prices rise significantly. The manager wants maximum exposure to capture the most capital appreciation.",
      result: "Fund value rises more when rates fall. Higher duration = higher upside.",
      action: "Increase Duration ↑",
    },
    rise: {
      label: "Expect Rates to RISE", color: red, icon: "📈",
      strategy: "Buy short-term bonds → Decrease Duration",
      why: "Short-duration bonds are less sensitive to rate increases — their prices fall less. The manager wants to minimize damage from rising rates.",
      result: "Fund value falls less when rates rise. Lower duration = lower downside.",
      action: "Decrease Duration ↓",
    },
  };
  const cfg = configs[expectation];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>⚙️ Duration Strategy Simulator</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        A portfolio manager's most powerful tool is adjusting the fund's <strong style={{ color: textCol }}>duration</strong> — the sensitivity to interest rate changes — based on their rate outlook.
      </p>
      <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
        {Object.entries(configs).map(([key, val]) => (
          <button key={key} onClick={() => setExpectation(key)} style={{ flex: 1, padding: "12px 16px", background: expectation === key ? navy4 : navy3, border: `1px solid ${expectation === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, color: expectation === key ? val.color : muted, transition: "all 0.2s" }}>{val.icon} {val.label}</button>
        ))}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {[["Strategy", cfg.strategy], ["Why", cfg.why], ["Result", cfg.result]].map(([lbl, val], i) => (
          <div key={i} style={{ background: navy4, borderLeft: `3px solid ${cfg.color}`, borderRadius: 6, padding: "13px 18px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: cfg.color, marginBottom: 6 }}>{lbl}</div>
            <div style={{ fontSize: "0.87rem", color: textCol, lineHeight: 1.7 }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, textAlign: "center", background: `${cfg.color}0d`, border: `1px solid ${cfg.color}33`, borderRadius: 8, padding: "14px" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", color: cfg.color, fontWeight: 700 }}>{cfg.action}</div>
      </div>
    </div>
  );
}

function BondFundTypes() {
  const [active, setActive] = useState(null);
  const types = [
    {
      icon: "⏱️", name: "Short-Term Bond Fund", color: blue2, badge: "< 5 Years",
      desc: "Invests in bonds maturing in less than 5 years. Low duration means low volatility. A step up in yield from money market funds with moderate interest rate risk.",
      comparison: "Alternative to money market funds for investors seeking slightly higher yield with modest additional risk.",
      pills: ["Low Duration", "Low Volatility", "< 5 yr Maturity"],
    },
    {
      icon: "🌍", name: "Global Bond Fund", color: gold, badge: "Foreign Bonds",
      desc: "Invests in bonds issued outside Canada — government and corporate. Adds geographic diversification but introduces currency risk.",
      comparison: "If the Canadian dollar rises vs. foreign currencies, the value of foreign bonds falls when converted back to CAD.",
      pills: ["Currency Risk", "Geographic Diversification", "FX Exposure"],
    },
  ];

  return (
    <div style={{ margin: "16px 0", display: "grid", gap: 10 }}>
      {types.map((t, i) => (
        <div key={i} onClick={() => setActive(active === i ? null : i)}
          style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? t.color + "55" : border}`, borderRadius: 8, padding: "16px 20px", cursor: "pointer", transition: "all 0.2s" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: "1.3rem" }}>{t.icon}</span>
              <div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: white }}>{t.name}</div>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: t.color, background: `${t.color}18`, border: `1px solid ${t.color}33` }}>{t.badge}</span>
              </div>
            </div>
            <span style={{ color: active === i ? t.color : muted, fontSize: "1rem", display: "inline-block", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s" }}>›</span>
          </div>
          {active === i && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${border}` }}>
              <div style={{ fontSize: "0.86rem", color: textCol, lineHeight: 1.7, marginBottom: 10 }}>{t.desc}</div>
              <div style={{ background: navy3, borderLeft: `3px solid ${t.color}`, borderRadius: 6, padding: "10px 14px", fontSize: "0.82rem", color: muted, fontStyle: "italic", marginBottom: 10 }}>💡 {t.comparison}</div>
              <div>{t.pills.map((p, j) => <Pill key={j} text={p} color={t.color} />)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Section 4: Preferred Dividend Funds ──
function PreferredDividendPanel() {
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { icon: "📊", title: "What It Holds", body: "Preferred shares of Canadian corporations — fixed dividend, senior to common shares, but below bondholders in bankruptcy.", color: purple },
          { icon: "💸", title: "Tax Advantage", body: "Income is taxed as Canadian dividends — eligible for the Dividend Tax Credit. More tax-efficient than interest from bond funds for investors in high tax brackets.", color: gold },
          { icon: "⚠️", title: "Risks vs. Bond Funds", body: "Higher risk — preferred shares are equity (lower rank in bankruptcy than bonds) AND prices are still sensitive to interest rate changes.", color: red },
          { icon: "🎯", title: "Best Suited For", body: "High-income investors holding funds outside a registered account (TFSA/RRSP) who want tax-efficient monthly income.", color: green },
        ].map((card, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "20px 18px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: card.color }} />
            <div style={{ fontSize: "1.3rem", marginBottom: 8 }}>{card.icon}</div>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 6 }}>{card.title}</div>
            <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.65 }}>{card.body}</div>
          </div>
        ))}
      </div>
      {/* Tax comparison visual */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 22 }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 14 }}>Income Tax Treatment — Bond Fund vs. Preferred Dividend Fund (40% bracket)</div>
        <div style={{ display: "grid", gap: 10 }}>
          {[
            { label: "Bond Fund — Interest Income", tax: "$400", after: "$600", pct: 100, color: red, note: "100% taxable" },
            { label: "Preferred Dividend Fund — Dividend Income", tax: "~$345", after: "~$655", pct: 75, color: gold, note: "Gross-up + DTC reduces effective rate" },
          ].map((row, i) => (
            <div key={i} style={{ background: navy4, borderRadius: 6, padding: "12px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontSize: "0.82rem", color: textCol }}>{row.label}</span>
                <span style={{ fontSize: "0.72rem", color: row.color, background: `${row.color}18`, border: `1px solid ${row.color}33`, padding: "2px 8px", borderRadius: 100 }}>{row.note}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ flex: 1, height: 8, background: navy3, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${row.pct}%`, background: row.color, borderRadius: 4, transition: "width 0.6s ease" }} />
                </div>
                <span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: row.color, width: 55, textAlign: "right" }}>Tax: {row.tax}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: "0.78rem", color: muted, marginTop: 10 }}>On $1,000 of income at a 40% marginal rate. Dividend Tax Credit makes preferred dividend income meaningfully more tax-efficient than interest income.</div>
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which of the following mutual funds is NOT insured by the Canada Deposit Insurance Corporation (CDIC)?", opts: ["Bank GICs", "Term Deposits at a bank", "Money Market Mutual Funds", "Savings Accounts"], correct: 2, fb: { y: "Correct! Mutual funds — including money market funds — are never CDIC insured. CDIC only covers deposits at member institutions (banks, trusts). This is a critical distinction.", n: "Not quite. Mutual funds are NEVER CDIC insured, regardless of what they invest in. Only deposits at CDIC member institutions qualify." } },
  { q: "If a bond fund manager expects interest rates to FALL, what strategy should they employ?", opts: ["Decrease the portfolio's duration", "Increase the portfolio's duration", "Sell all bonds and move to cash", "Buy short-term T-bills only"], correct: 1, fb: { y: "Correct! Longer duration = more price sensitivity. When rates fall, long-duration bonds rise the most. The manager increases duration to capture maximum capital appreciation.", n: "Not quite. If rates are expected to fall, increase duration by buying long-term bonds — this maximizes capital gains as prices rise." } },
  { q: "Calculate the Current Yield if the 7-day yield of a money market fund is 0.10% (0.001).", opts: ["5.21%", "0.10%", "3.65%", "4.80%"], correct: 0, fb: { y: "Correct! Current Yield = 7-day yield × (365÷7) = 0.001 × 52.14 = 5.21%. This is the simple annualization — no compounding assumed.", n: "Not quite. Current Yield = 7-day yield × (365÷7) = 0.001 × 52.14 = 0.05214 = 5.21%. Multiply by the number of 7-day periods in a year." } },
  { q: "Which risk is most associated with Money Market Funds held for long periods?", opts: ["Default Risk", "Inflation Risk", "Liquidity Risk", "Market Risk"], correct: 1, fb: { y: "Correct! Money market funds invest in high-quality, safe instruments — default risk is minimal. The main danger is inflation risk: returns may be so low they don't keep up with the cost of living.", n: "Not quite. Money market funds are extremely safe from default and always liquid. The key risk is inflation risk — low returns may not preserve purchasing power over time." } },
  { q: "What is the primary advantage of a Preferred Dividend Fund over a Bond Fund for a high-income earner?", opts: ["Higher safety of principal", "Guaranteed returns", "The Dividend Tax Credit", "Shorter duration"], correct: 2, fb: { y: "Correct! Preferred dividend fund income qualifies for the Dividend Tax Credit — making it more tax-efficient than the 100% taxable interest income generated by bond funds.", n: "Not quite. The key advantage is the Dividend Tax Credit. Preferred shares pay dividends (not interest), which are taxed at a preferred rate after the gross-up and DTC." } },
  { q: "'Duration' is best described as:", opts: ["The time until a bond matures", "A measure of a bond portfolio's sensitivity to interest rate changes", "The length of time a client should hold a fund", "The frequency of coupon payments"], correct: 1, fb: { y: "Correct! Duration measures price sensitivity to interest rate changes — not time to maturity. Two bonds can have the same maturity but different durations depending on their coupon rates.", n: "Not quite. Duration ≠ maturity. Duration is a sensitivity measure — how much the bond's price will move when interest rates change by 1%." } },
  { q: "Why might a Mortgage Fund's NAVPU drop?", opts: ["Mortgage interest rates have decreased", "Mortgage interest rates have increased", "Borrowers are paying off their loans early", "The fund manager bought more mortgages"], correct: 1, fb: { y: "Correct! Inverse relationship — rising rates make the fund's older, lower-rate mortgages less valuable compared to new mortgages. NAV drops.", n: "Not quite. Rising mortgage rates hurt the fund — existing mortgages at lower rates are now worth less relative to new, higher-rate mortgages." } },
  { q: "The 'Effective Yield' of a money market fund differs from the 'Current Yield' because:", opts: ["It assumes reinvestment (compounding) of income weekly", "It uses a 360-day year instead of 365", "It excludes management fees", "It is government-guaranteed"], correct: 0, fb: { y: "Correct! Effective Yield assumes weekly compounding — the 7-day return is reinvested and compounds 52 times a year. This is why Effective Yield is always higher than Current Yield.", n: "Not quite. Effective Yield assumes weekly compounding. Current Yield is simple annualization (no compounding). Compounding always produces a slightly higher result." } },
  { q: "A 'Short-Term Bond Fund' typically invests in bonds with maturities of:", opts: ["Less than 90 days", "Less than 5 years", "10 to 20 years", "Over 20 years"], correct: 1, fb: { y: "Correct! Short-term bond funds hold bonds maturing in less than 5 years. This gives low duration and low volatility — a middle ground between money market funds and full bond funds.", n: "Not quite. Short-term bond funds use bonds with maturities under 5 years. Under 90 days describes money market instruments, not bond funds." } },
  { q: "National Instrument 81-102 requires a Money Market Fund to maintain a weighted average term to maturity of no more than:", opts: ["365 days", "180 days", "90 days", "7 days"], correct: 2, fb: { y: "Correct! NI 81-102 caps the weighted average term to maturity at 90 days for money market funds. Individual securities can be up to 364 days, but the portfolio average must stay at 90 days or less.", n: "Not quite. NI 81-102 sets the weighted average maturity cap at 90 days. Individual securities can be up to 364 days, but the blend must average 90 days or less." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 11 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered fixed-income mutual funds." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit duration strategy and yield calculations." : "Focus on the yield formulas, duration rules, and CDIC exclusion."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Money Market Fund", def: "Most conservative fund type. Invests ≥95% in short-term debt (≤364 days). Fixed $10 unit price. Not CDIC insured." },
  { term: "NI 81-102", def: "National Instrument regulating mutual fund holdings. Limits money market funds to high-quality short-term debt; max 90-day weighted average maturity." },
  { term: "Weighted Average Maturity", def: "The average term to maturity of a portfolio's holdings, weighted by size. Money market funds: max 90 days." },
  { term: "Current Yield", def: "7-Day Yield × (365÷7). Annualizes the 7-day return with no compounding assumption." },
  { term: "Effective Yield", def: "(1 + 7-Day Yield)^(365/7) − 1. Assumes weekly reinvestment/compounding. Always higher than Current Yield." },
  { term: "Inflation Risk", def: "The risk that returns do not keep pace with the cost of living — the primary risk of money market funds held long-term." },
  { term: "Open Mortgage", def: "Can be repaid early without penalty. Lower interest rate; higher prepayment risk for the lender/fund." },
  { term: "Closed Mortgage", def: "Cannot be repaid early without a penalty. Higher interest rate; lower prepayment risk for the fund." },
  { term: "NHA-Insured Mortgage", def: "Guaranteed by the federal government against borrower default. Most mortgage funds hold these to minimize credit risk." },
  { term: "Prepayment Risk", def: "Risk that mortgages in the fund are repaid early — forcing reinvestment at lower prevailing rates." },
  { term: "Mortgage Fund NAV", def: "Falls when mortgage rates rise (inverse relationship) — existing lower-rate mortgages become less valuable." },
  { term: "Duration", def: "A measure (in years) of a bond portfolio's sensitivity to interest rate changes. High duration = more volatile." },
  { term: "Interest Rate Risk", def: "The risk that rising rates cause bond (or mortgage) prices to fall. Amplified by high duration." },
  { term: "Duration Strategy (Fall)", def: "If rates expected to fall → increase duration (buy long-term bonds) → maximize capital appreciation." },
  { term: "Duration Strategy (Rise)", def: "If rates expected to rise → decrease duration (buy short-term bonds) → minimize price decline." },
  { term: "Short-Term Bond Fund", def: "Holds bonds with maturities < 5 years. Low duration, low volatility. Step up from money market funds." },
  { term: "Global Bond Fund", def: "Invests in foreign bonds. Adds currency risk — a rising CAD reduces the CAD value of foreign holdings." },
  { term: "Currency Risk", def: "Risk that exchange rate movements reduce the Canadian-dollar value of foreign investments." },
  { term: "Preferred Dividend Fund", def: "Holds preferred shares. Income taxed as dividends (Dividend Tax Credit). Higher risk than bond funds." },
  { term: "Dividend Tax Credit (DTC)", def: "Reduces tax payable on eligible Canadian dividends. Makes preferred dividend fund income more tax-efficient than bond fund interest." },
];

// ── Main Export ──
export default function Chapter11() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Fund Type Overview — Risk Spectrum"],
    ["02", "Money Market Funds — Structure, Holdings & Yields"],
    ["03", "Mortgage Mutual Funds — Rate Sensitivity"],
    ["04", "Bond Funds — Duration Strategy"],
    ["05", "Preferred Dividend Funds — Tax Efficiency"],
    ["🧮", "Money Market Yield Calculator (7-Day, Current, Effective)"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 11
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Fixed-Income <span style={{ color: gold }}>Mutual Funds</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Money market, mortgage, bond, and preferred dividend funds — their objectives, holdings, risks, and the key mechanics of duration strategy and yield calculation.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","4","Fund Types"],["⏱","~16 min","read"],["🎯","10","practice questions"],["🧮","1","yield calculator"],["📊","2","interactive simulators"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1: Overview ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Fund Spectrum</span><div style={s.line} /></div>
            <h2 style={s.h2}>Fixed-Income Fund Overview</h2>
            <p style={s.prose}>
              Chapter 11 covers four fund types that exist on a <span style={{ color: gold, fontWeight: 600 }}>risk-return spectrum</span> — from the most conservative (money market) to the most income-focused (preferred dividend). Click any fund on the spectrum below to see its full profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}><FundSpectrum /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2: Money Market ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Money Market Funds</span><div style={s.line} /></div>
            <h2 style={s.h2}>Money Market Mutual Funds</h2>
            <p style={s.prose}>
              The most conservative mutual fund type. Must invest at least <span style={{ color: gold, fontWeight: 600 }}>95% of net assets in short-term, high-quality debt</span> with maturities of 364 days or less. The portfolio's weighted average maturity is capped at 90 days by NI 81-102.
            </p>
          </Reveal>
          <Reveal delay={0.1}><MoneyMarketDetail /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📊" accent={blue2}>
              <strong>Why Report Yield, Not Return?</strong> Because the unit price ($10) almost never changes, a percentage return would always show near-zero. Instead, money market funds report <strong>Current Yield</strong> (simple annualization) and <strong>Effective Yield</strong> (with compounding) — both derived from the fund's 7-day net income. Effective Yield is always higher because it accounts for weekly reinvestment.
            </Callout>
          </Reveal>
          <Reveal delay={0.3}><MoneyMarketYieldCalc /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3: Mortgage ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Mortgage Funds</span><div style={s.line} /></div>
            <h2 style={s.h2}>Mortgage Mutual Funds</h2>
            <p style={s.prose}>
              Invest in a diversified portfolio of residential and commercial mortgages. Although mortgages are loans (not bonds), a mortgage fund behaves like a bond fund — it has an <span style={{ color: gold, fontWeight: 600 }}>inverse relationship with interest rates</span>. When rates rise, existing lower-rate mortgages decline in value, and the fund's NAV drops.
            </p>
          </Reveal>
          <Reveal delay={0.1}><MortgageTypes /></Reveal>
          <Reveal delay={0.2}><MortgageRateViz /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="🏛️" accent={green}>
              <strong style={{ color: green }}>NHA Insurance:</strong> Most mortgage funds invest heavily in National Housing Act (NHA) insured mortgages — backed by the federal government against borrower default. This eliminates credit risk, leaving interest rate risk as the primary concern.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4: Bond Funds ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>Bond Funds</span><div style={s.line} /></div>
            <h2 style={s.h2}>Bond Funds &amp; Duration Strategy</h2>
            <p style={s.prose}>
              Bond funds invest in government and corporate bonds with terms longer than one year. The key concept is <span style={{ color: gold, fontWeight: 600 }}>duration</span> — the measure of how sensitive the fund is to interest rate changes. A skilled manager actively adjusts duration based on their rate outlook:
            </p>
          </Reveal>
          <Reveal delay={0.1}><DurationStrategy /></Reveal>
          <Reveal delay={0.2}><BondFundTypes /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="⚡">
              <strong>Duration Quick Reference:</strong> Long maturity + low coupon = high duration = high sensitivity. Short maturity + high coupon = low duration = low sensitivity. A fund expecting rates to fall wants HIGH duration; a fund expecting rates to rise wants LOW duration.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S5: Preferred ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 05</span><span style={s.badge}>Preferred Dividend Funds</span><div style={s.line} /></div>
            <h2 style={s.h2}>Preferred Dividend Funds</h2>
            <p style={s.prose}>
              These funds invest in preferred shares of Canadian corporations. The defining advantage over bond funds is that income is classified as <span style={{ color: gold, fontWeight: 600 }}>Canadian dividends</span> — qualifying for the Dividend Tax Credit, which makes them more tax-efficient than interest-paying bond funds for high-income investors:
            </p>
          </Reveal>
          <Reveal delay={0.1}><PreferredDividendPanel /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering all four fund types, yield calculations, duration strategy, and the tax treatment of distributions from Chapter 11.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 11 — money market, mortgage, bond, and preferred dividend funds.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
